/**
 * Temas da área de conversa.
 *
 * Um preset descreve a **superfície inteira** da conversa, não só a imagem de fundo. Isso não é
 * exagero de escopo: trocar apenas o fundo de `.messages` para escuro deixaria a faixa do compositor
 * branca embaixo (`.composer`, `.input` e `.footer` eram `#fff` fixos), e o resultado parecia dois
 * aplicativos costurados. Canvas e moldura andam juntos ou não andam.
 *
 * Mora no engine, e não no SDK, porque os dois lados precisam da tabela: o SDK monta o CSS, e o painel
 * precisa dos rótulos e das cores recomendadas para o seletor da aba Estilo.
 *
 * Sobre o padrão de rabiscos: o do WhatsApp é arte da Meta. O nosso é autoral, desenhado no mesmo
 * espírito — ver `doodle.ts` no pacote do SDK.
 */
/*
 * Procedência dos ladrilhos, que é bom estar escrito perto de onde eles são referenciados:
 *
 * `wa-tile-dark` é o fundo que o WhatsApp Web usa no tema escuro
 * (`web.whatsapp.com/img/bg-chat-tile-dark_*.png`), 540×981, PNG indexado de 4 bits, 86 KB. É **arte
 * da Meta**, não nossa, e está no projeto por decisão de produto. Se precisar sair por licenciamento,
 * o caminho é trocar os dois `pattern` para `kind: 'doodle'` e ajustar a cor do traço.
 *
 * `wa-tile-light` é gerado a partir dele por `scripts/build-light-tile.mjs`: os pixels são os mesmos,
 * só a paleta vira preta. O traço do ladrilho original é branco, e branco sobre bege clareia em vez de
 * desenhar — no tema claro o traço precisa ser escuro. Como a arte inteira vive no canal alpha, trocar
 * a cor é reescrever a PLTE e nada mais.
 *
 * O sufixo no nome são 8 caracteres do SHA-256 do arquivo. A URL é cacheada por um ano, então trocar
 * os bytes no mesmo caminho deixaria parte dos visitantes com a versão antiga: textura nova = nome
 * novo, e o arquivo antigo continua publicado enquanto houver widget apontando para ele.
 */
const WHATSAPP_TILE_DARK = 'wa-tile-dark.b0fa5029.png';
const WHATSAPP_TILE_LIGHT = 'wa-tile-light.b6fb1ab1.png';
/**
 * `plain` reproduz exatamente o visual anterior a este recurso. É a saída de emergência para marca
 * que não quer fundo escuro, e é o que mantém os testes de estilo antigos com o mesmo significado.
 */
export const CHAT_SURFACES = {
    'whatsapp-dark': {
        id: 'whatsapp-dark',
        label: 'WhatsApp escuro',
        /*
         * `#0b141a`, e não `#111b21`. Os dois existem no tema escuro do WhatsApp e é fácil trocar: o
         * `#111b21` é o fundo do **aplicativo** (barra lateral, lista de conversas), e o `#0b141a` é o
         * fundo da **área de conversa**, que é o que este canvas representa. Conferido contra um print
         * de referência do WhatsApp Web escuro.
         */
        canvas: '#0b141a',
        /*
         * `#0b141ad1`: o **mesmo** canvas com alpha 0xD1 (82%). O traço mais forte do ladrilho tem
         * alpha 90%, e 0,90 × (1 − 0,82) ≈ 16% de branco. Mexer neste único byte é o jeito de deixar a
         * textura mais ou menos visível — era 0xEC (≈6,7%) antes, mais discreto que isto.
         *
         * O véu precisa acompanhar o canvas: com cores diferentes ele deixaria de ser um véu e viraria
         * uma camada de outra cor por cima da textura.
         */
        pattern: { kind: 'tile', file: WHATSAPP_TILE_DARK, veil: '#0b141ad1' },
        surface: '#202c33',
        surfaceBorder: '#2a3942',
        inputBg: '#2a3942',
        inputBorder: '#2a3942',
        muted: '#8696a0',
        danger: '#f8a5a5',
        outgoing: { bubble: '#005c4b', text: '#e9edef', meta: 'rgba(233,237,239,0.6)' },
        // Cabeçalho segue na cor da marca aqui: só o tema claro pediu o verde-escuro do aplicativo.
        header: null,
        avatar: null,
        meta: 'rgba(233,237,239,0.6)',
        tick: '#53bdeb',
        recommendedBubble: '#223038',
        recommendedText: '#e9edef',
    },
    'whatsapp-light': {
        id: 'whatsapp-light',
        label: 'WhatsApp claro',
        canvas: '#efeae2',
        /*
         * `0xDB` = 85,9%: traço preto a 0,90 × 0,141 ≈ 13% sobre o creme.
         *
         * Passou por 0xF0 (≈5,3%, discreto demais) e 0xCF (≈17%, forte demais) até este meio-termo.
         * Fica um pouco abaixo dos ~16% do tema escuro, e é intencional: traço escuro sobre creme pesa
         * mais na vista que traço claro sobre o quase-preto.
         */
        pattern: { kind: 'tile', file: WHATSAPP_TILE_LIGHT, veil: '#efeae2db' },
        surface: '#f0f2f5',
        surfaceBorder: '#d1d7db',
        inputBg: '#ffffff',
        inputBorder: '#d1d7db',
        muted: '#667781',
        danger: '#b91c1c',
        outgoing: { bubble: '#d9fdd3', text: '#111b21', meta: 'rgba(17,27,33,0.45)' },
        /*
         * Verde-escuro no cabeçalho e verde vivo no círculo do glifo — o mesmo par do ícone do
         * aplicativo. O branco translúcido de 22% que o círculo usa por padrão sumiria sobre este
         * cabeçalho escuro.
         */
        header: '#085b52',
        avatar: '#2ce52c',
        meta: 'rgba(17,27,33,0.45)',
        tick: '#53bdeb',
        recommendedBubble: '#ffffff',
        recommendedText: '#111b21',
    },
    plain: {
        id: 'plain',
        label: 'Branco liso',
        canvas: '#ffffff',
        pattern: null,
        surface: '#ffffff',
        surfaceBorder: '#e5e7eb',
        inputBg: '#ffffff',
        inputBorder: '#d1d5db',
        muted: '#94a3b8',
        danger: '#b91c1c',
        // `null` de propósito: aqui tudo segue na cor da marca, como antes destes recursos.
        outgoing: null,
        header: null,
        avatar: null,
        meta: 'rgba(15,23,42,0.45)',
        tick: '#53bdeb',
        recommendedBubble: '#f1f5f9',
        recommendedText: '#0f172a',
    },
};
export const DEFAULT_CHAT_BACKGROUND = 'whatsapp-dark';
/** Ordem do seletor no painel. */
export const CHAT_SURFACE_LIST = [
    CHAT_SURFACES['whatsapp-dark'],
    CHAT_SURFACES['whatsapp-light'],
    CHAT_SURFACES.plain,
];
/**
 * Resolve o tema, com o padrão cobrindo dois casos.
 *
 * **Ausente** é o caso que importa: webchat publicado antes deste recurso não tem o campo, e é
 * justamente por isso que ele herda o fundo novo sem precisar republicar.
 *
 * **Desconhecido** cai no mesmo lugar porque a config chega por vários caminhos (rede, preview do
 * painel, objeto montado à mão em teste) e CSS quebrado é pior que fundo inesperado.
 */
export function resolveChatSurface(value) {
    if (value && Object.prototype.hasOwnProperty.call(CHAT_SURFACES, value))
        return CHAT_SURFACES[value];
    return CHAT_SURFACES[DEFAULT_CHAT_BACKGROUND];
}
//# sourceMappingURL=chatBackground.js.map